<template>
  <b-modal
    id="client-feed-edit-modal"
    :title="feed ? `Edit: ${feed.legacy_client}` : ''"
    size="lg"
    :busy="saving"
    @hidden="onHidden"
    @ok.prevent="save"
    ok-title="Save"
    cancel-title="Cancel"
  >
    <div v-if="feed">
      <b-alert v-if="errorMessage" variant="danger" show>{{
        errorMessage
      }}</b-alert>

      <b-form-group label="Status">
        <b-form-radio-group
          v-model="form.status"
          :options="statusOptions"
          :disabled="saving"
          buttons
          button-variant="outline-primary"
          size="sm"
        />
      </b-form-group>

      <b-form-group
        label="Application method"
        description="Leave as 'Use default' to keep the feed's class-defined behavior."
      >
        <b-form-select
          v-model="form.application_method"
          :options="applicationMethodOptions"
          :disabled="saving"
        />
      </b-form-group>

      <b-form-group>
        <b-form-checkbox
          v-model="form.ai_noc_matching_enabled"
          :disabled="saving"
        >
          NOC matching enabled
        </b-form-checkbox>
      </b-form-group>

      <b-form-group>
        <b-form-checkbox v-model="form.auto_publish" :disabled="saving">
          Auto-publish imported offers (otherwise they land in Pending Review)
        </b-form-checkbox>
      </b-form-group>

      <b-form-group
        description="Push imported offers to Indeed when published and expire them when they leave the feed. Requires the company's Indeed sync flag too. Only applies to auto-published feeds."
      >
        <b-form-checkbox
          v-model="form.is_indeed_sync_enabled"
          :disabled="saving"
        >
          Sync to Indeed (push / expire from feed)
        </b-form-checkbox>
      </b-form-group>

      <b-form-group
        label="Notification email"
        description="Where feed errors are sent. Leave blank for none."
      >
        <b-form-input
          v-model="form.notification_email"
          type="email"
          :disabled="saving"
          placeholder="ops@example.com"
        />
      </b-form-group>

      <b-form-group label="Default jobboards — Internal">
        <b-form-checkbox-group
          v-model="form.default_jobboards"
          :options="jobboardOptions.internal"
          :disabled="saving"
          stacked
          class="jobboard-grid"
        />
      </b-form-group>

      <b-form-group label="Default jobboards — External aggregators">
        <b-form-checkbox-group
          v-model="form.default_jobboards"
          :options="jobboardOptions.external"
          :disabled="saving"
          stacked
          class="jobboard-grid"
        />
      </b-form-group>

      <b-form-group
        label="Recruiters (application alerts)"
        description="These users receive candidate application alerts for this feed. They must have application alerts enabled to actually be notified."
      >
        <b-form-checkbox-group
          v-model="form.default_recruiter_ids"
          :options="recruiterOptions"
          :disabled="saving"
          stacked
        />
      </b-form-group>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button variant="secondary" :disabled="saving" @click="cancel()"
        >Cancel</b-button
      >
      <b-button variant="primary" :disabled="saving" @click="ok()">
        <b-spinner v-if="saving" small class="mr-1" />
        Save
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BFormCheckbox,
  BFormCheckboxGroup,
  BFormRadioGroup,
  BButton,
  BSpinner,
  BAlert
} from "bootstrap-vue";

const emptyForm = () => ({
  status: "active",
  application_method: null,
  ai_noc_matching_enabled: false,
  auto_publish: false,
  is_indeed_sync_enabled: false,
  notification_email: "",
  default_jobboards: [],
  default_recruiter_ids: []
});

export default {
  name: "ClientFeedEditModal",
  components: {
    BModal,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BFormCheckbox,
    BFormCheckboxGroup,
    BFormRadioGroup,
    BButton,
    BSpinner,
    BAlert
  },
  props: {
    feed: { type: Object, default: null },
    jobboardOptions: {
      type: Object,
      default: () => ({ internal: [], external: [] })
    },
    availableRecruiters: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: emptyForm(),
      saving: false,
      errorMessage: null,
      statusOptions: [
        { text: "Active", value: "active" },
        { text: "Suspend", value: "suspend" }
      ],
      applicationMethodOptions: [
        { text: "— Use default —", value: null },
        { text: "Internal", value: "internal" },
        { text: "External", value: "external" },
        { text: "External only", value: "external_only" }
      ]
    };
  },
  computed: {
    recruiterOptions() {
      return this.availableRecruiters.map(u => ({
        value: u.id,
        text:
          `${u.name || u.email} (${u.email})` +
          (u.should_receive_application_alert ? "" : " — ⚠ alerts off")
      }));
    }
  },
  watch: {
    feed: {
      immediate: true,
      handler(newFeed) {
        if (!newFeed) {
          this.form = emptyForm();
          return;
        }
        const incoming = Array.isArray(newFeed.default_jobboards)
          ? newFeed.default_jobboards
          : [];
        const allKnown = [
          ...(this.jobboardOptions.internal || []),
          ...(this.jobboardOptions.external || [])
        ];
        this.form = {
          status: newFeed.status,
          application_method: newFeed.application_method,
          ai_noc_matching_enabled: !!newFeed.ai_noc_matching_enabled,
          auto_publish: !!newFeed.auto_publish,
          is_indeed_sync_enabled: !!newFeed.is_indeed_sync_enabled,
          notification_email: newFeed.notification_email || "",
          default_jobboards: incoming.filter(v => allKnown.includes(v)),
          default_recruiter_ids: Array.isArray(newFeed.default_recruiter_ids)
            ? newFeed.default_recruiter_ids
            : []
        };
        this.errorMessage = null;
      }
    },
    jobboardOptions: {
      handler(newOptions) {
        if (!this.feed) return;
        const incoming = Array.isArray(this.feed.default_jobboards)
          ? this.feed.default_jobboards
          : [];
        const allKnown = [
          ...(newOptions.internal || []),
          ...(newOptions.external || [])
        ];
        this.form.default_jobboards = incoming.filter(v =>
          allKnown.includes(v)
        );
      }
    }
  },
  methods: {
    save() {
      if (!this.feed) return;
      this.saving = true;
      this.errorMessage = null;

      const payload = {
        status: this.form.status,
        application_method: this.form.application_method,
        ai_noc_matching_enabled: this.form.ai_noc_matching_enabled,
        auto_publish: this.form.auto_publish,
        is_indeed_sync_enabled: this.form.is_indeed_sync_enabled,
        notification_email: this.form.notification_email || null,
        default_jobboards: this.form.default_jobboards,
        default_recruiter_ids: this.form.default_recruiter_ids
      };

      this.$http
        .put(`/enroll_admin/client-feeds/${this.feed.id}`, payload)
        .then(({ data }) => {
          this.$emit("updated", data);
          this.$bvModal.hide("client-feed-edit-modal");
        })
        .catch(err => {
          this.errorMessage = err?.response?.data?.message || "Failed to save.";
        })
        .finally(() => {
          this.saving = false;
        });
    },
    onHidden() {
      this.$emit("hidden");
    }
  }
};
</script>

<style scoped>
.jobboard-grid >>> .custom-control {
  display: inline-block;
  margin-right: 1rem;
  min-width: 180px;
}
</style>
